import React, { useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';

type Props = {
  className?: string;
  open?: boolean;
  fluid?: boolean;
  on?: 'click' | 'hover';
  children: React.ReactNode;
  trigger: JSX.Element;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  position?:
    | 'top center'
    | 'top left'
    | 'top right'
    | 'bottom center'
    | 'bottom right'
    | 'bottom left'
    | 'right center'
    | 'right top'
    | 'right bottom'
    | 'left top'
    | 'left bottom'
    | 'left center';
  unmount?: boolean;
};

function Popup({
  className = 'py-1 px-2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 outline-none',
  open,
  fluid = false,
  on = 'click',
  children,
  trigger,
  position = 'bottom center',
  disabled = false,
  unmount = false,
  onOpen,
  onClose,
}: Props) {
  const floaterRef = useRef();
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open === undefined ? internalOpen : open;

  const popupClasses = ['absolute', 'z-10'];
  const popupStyles = {};
  const innerPopupClasses = [];
  switch (position) {
    case 'bottom center':
      popupClasses.push('mt-1', 'left-1/2');
      innerPopupClasses.push('relative', '-left-1/2');
      break;
    case 'bottom left':
      popupClasses.push('mt-1', 'left-0');
      break;
    case 'bottom right':
      popupClasses.push('mt-1', 'right-0');
      break;
    case 'top center':
      popupClasses.push('mb-1', 'bottom-full', 'left-1/2');
      innerPopupClasses.push('relative', '-left-1/2');
      break;
    case 'top left':
      popupClasses.push('mb-1', 'bottom-full', 'left-0');
      break;
    case 'top right':
      popupClasses.push('mb-1', 'bottom-full', 'right-0');
      break;
    case 'left center':
      popupClasses.push('mr-1', 'right-full', 'top-0');
      popupStyles.top = '50%';
      popupStyles.transform = 'translateY(-50%)';
      break;
    case 'left top':
      popupClasses.push('mr-1', 'right-full', 'top-0');
      break;
    case 'left bottom':
      popupClasses.push('mr-1', 'right-full', 'bottom-0');
      break;
    case 'right center':
      popupClasses.push('ml-1', 'left-full', 'top-0');
      popupStyles.top = '50%';
      popupStyles.transform = 'translateY(-50%)';
      break;
    case 'right top':
      popupClasses.push('ml-1', 'left-full', 'top-0');
      break;
    case 'right bottom':
      popupClasses.push('ml-1', 'left-full', 'bottom-0');
      break;
    default:
      // bottom left
      popupClasses.push('mt-1', 'left-0');
      break;
  }

  if (fluid) {
    popupClasses.push('min-w-full');
  }

  const listener = useRef<(() => void) | undefined | null>();
  const setListener = (func: () => void) => {
    document.addEventListener('click', func, false);
    listener.current = func;
  };

  useEffect(() => {
    if (listener.current && !isOpen) {
      document.removeEventListener('click', listener.current, false);
      listener.current = null;
    }
    if (!listener.current && isOpen) {
      const hide = (e: MouseEvent) => {
        if (!e.composedPath().includes(floaterRef?.current)) {
          setInternalOpen(false);
          if (onClose) {
            onClose();
          }
        }
      };
      setListener(hide);
    }
  }, [open, internalOpen]);

  const triggerOnClick = (e) => {
    e.stopPropagation();

    if (on === 'click') {
      setInternalOpen(true);

      const hide = (e: MouseEvent) => {
        if (!e.composedPath().includes(floaterRef?.current)) {
          setInternalOpen(false);
          if (onClose) {
            onClose();
          }
        }
      };

      if (!isOpen && onOpen) {
        onOpen();
      } else if (isOpen && onClose) {
        onClose();
      }

      if (!isOpen) {
        setListener(hide);
      }
    }
  };

  const triggerOnMouseEnter = () => {
    if (on === 'hover') {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
        closeTimeout.current = null;
      }
      setInternalOpen(true);
      if (!isOpen && onOpen) onOpen();
    }
  };

  const triggerOnMouseLeave = () => {
    if (on === 'hover') {
      if (!closeTimeout.current)
        closeTimeout.current = setTimeout(() => {
          setInternalOpen(false);
          if (isOpen && onClose) onClose();
        }, 350);
    }
  };

  return (
    <div className={`relative inline-block ${fluid && 'w-full'}`}>
      <div
        className="contents"
        role="presentation"
        tabIndex={-1}
        onClick={triggerOnClick}
        onMouseOver={triggerOnMouseEnter}
        onFocus={triggerOnMouseEnter}
        onMouseOut={triggerOnMouseLeave}
        onBlur={triggerOnMouseLeave}
      >
        {trigger}
      </div>

      <Transition
        unmount={unmount}
        show={!disabled && isOpen}
        className={popupClasses.join(' ')}
        style={popupStyles}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div
          className={`${innerPopupClasses.join(' ')} ${className}`}
          ref={floaterRef}
          onMouseOver={triggerOnMouseEnter}
          onFocus={triggerOnMouseEnter}
          onMouseOut={triggerOnMouseLeave}
          onBlur={triggerOnMouseLeave}
        >
          {children}
        </div>
      </Transition>
    </div>
  );
}

export default Popup;
